import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Download, Target, Users, ArrowRight, CheckCircle2, Lightbulb, Send, Inbox } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NudgeDialog } from "@/components/NudgeDialog";
import { NudgeApprovals, NudgeResults } from "@/components/NudgeApprovals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/AppLayout";
import { api, formatErr } from "@/lib/api";

function StageCard({ s, i, tip, industry }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={`playbook-stage-${s.key}`}>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="font-mono text-xs text-emerald-700">STAGE {String(i + 1).padStart(2, "0")}</span>
        <h3 className="font-display text-xl font-bold text-slate-900 flex-1">{s.title}</h3>
        <Link to={s.page} className="text-xs font-semibold text-emerald-700 hover:underline flex items-center gap-1">{s.feature} <ArrowRight className="h-3 w-3" /></Link>
      </div>
      <p className="text-sm text-slate-700 mt-3"><b>Why it matters for funding:</b> {s.why}</p>
      <div className="grid md:grid-cols-2 gap-6 mt-4">
        <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer does</div><ul className="mt-2 space-y-1.5 text-sm text-slate-700 list-disc pl-5">{s.customer.map((c) => <li key={c}>{c}</li>)}</ul></div>
        <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Our team does</div><ul className="mt-2 space-y-1.5 text-sm text-slate-700 list-disc pl-5">{s.team.map((c) => <li key={c}>{c}</li>)}</ul></div>
      </div>
      {tip && <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-slate-800" data-testid={`playbook-tip-${s.key}`}><b className="text-amber-800 inline-flex items-center gap-1"><Lightbulb className="h-3.5 w-3.5" />{industry} tip:</b> {tip}</div>}
      <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />Complete when: {s.done}</p>
    </div>
  );
}

function Guide({ d, industry, setIndustry }) {
  return (
    <div className="space-y-4" data-testid="playbook-guide">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-slate-600">Show funding tips for</span>
        <Select value={industry || "none"} onValueChange={(v) => setIndustry(v === "none" ? "" : v)}>
          <SelectTrigger className="w-60 bg-white" data-testid="playbook-industry-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">No industry (general guide)</SelectItem>{d.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-slate-900 text-white rounded-lg p-6 flex gap-4"><Target className="h-6 w-6 text-amber-400 shrink-0" /><p className="text-sm leading-relaxed">{d.goal}</p></div>
      {d.stages.map((s, i) => <StageCard key={s.key} s={s} i={i} industry={industry} tip={industry && d.tips[industry]?.[s.key]} />)}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="playbook-admin-tools">
        <h3 className="font-display text-xl font-bold text-slate-900">Admin tools</h3>
        <ul className="mt-3 space-y-2 text-sm">{d.admin_tools.map((t) => <li key={t.name}><b className="text-slate-900">{t.name}:</b> <span className="text-slate-700">{t.desc}</span></li>)}</ul>
      </div>
    </div>
  );
}

function AutoToggle({ c }) {
  const [on, setOn] = useState(!!c.auto_nudge);
  const flip = async () => {
    try { await api.put(`/admin/journey/${c.user_id}/auto-nudge`, { enabled: !on }); setOn(!on); toast.success(!on ? "Vera will auto-send nudges for this customer" : "Auto-send off for this customer"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return <button onClick={flip} aria-pressed={on} title="Let Vera send nudges for this customer without approval" data-testid={`journey-auto-${c.user_id}`}
    className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 border transition-colors ${on ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-400 text-slate-600 hover:border-slate-600"}`}>AUTO</button>;
}

function CustomerCard({ c, onNudge }) {
  const recent = c.last_nudged_at && Date.now() - new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(c.last_nudged_at) ? c.last_nudged_at : `${c.last_nudged_at}Z`) < 864e5;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 text-xs" data-testid={`journey-card-${c.user_id}`}>
      <div className="flex items-center justify-between gap-2"><b className="text-sm text-slate-900 truncate">{c.company || c.name}</b><span className="font-mono text-emerald-700 font-bold" title="Ready to Apply score">{c.score}%</span></div>
      <div className="text-slate-500 truncate">{c.email}</div>
      <div className="text-slate-500 truncate">{[c.industry, c.state].filter(Boolean).join(" · ") || "No profile yet"}</div>
      <div className="mt-2 text-slate-700 border-t border-slate-100 pt-2"><span className="text-slate-400">Next: </span>{c.next}</div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <AutoToggle c={c} />
        <span className="text-[10px] text-slate-400" data-testid={`journey-nudged-${c.user_id}`}>{c.last_nudged_at && !recent ? `Nudged ${new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(c.last_nudged_at) ? c.last_nudged_at : `${c.last_nudged_at}Z`).toLocaleDateString()}` : ""}</span>
        {recent ? <span className="text-[11px] text-slate-400" data-testid={`journey-nudge-${c.user_id}`}>Nudged today</span> : <button onClick={() => onNudge(c)} className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1" data-testid={`journey-nudge-${c.user_id}`}><Send className="h-3 w-3" />Nudge</button>}
      </div>
    </div>
  );
}

function Board() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [nudge, setNudge] = useState(null);
  const load = () => api.get("/admin/journey").then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { api.get("/admin/journey").then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e))); }, []);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />;
  const match = (c) => !q || `${c.company} ${c.name} ${c.email} ${c.industry} ${c.state}`.toLowerCase().includes(q.toLowerCase());
  return (
    <div data-testid="journey-board">
      <NudgeResults />
      <div className="flex items-center gap-3 mb-4">
        <p className="text-sm text-slate-500 flex-1">{d.total} customer{d.total === 1 ? "" : "s"}, each shown at the first stage they haven't finished. Scroll sideways to see all 8 stages →</p>
        <Input className="w-64 bg-white" placeholder="Search customers" value={q} onChange={(e) => setQ(e.target.value)} data-testid="journey-search-input" />
      </div>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {d.columns.map((col, i) => {
          const list = col.customers.filter(match);
          return (
            <div key={col.key} className="w-64 shrink-0 bg-slate-100 rounded-lg p-3" data-testid={`journey-column-${col.key}`}>
              <div className="flex items-center justify-between mb-3"><span className="text-xs font-semibold text-slate-700">{i + 1}. {col.title}</span><span className="text-xs font-bold bg-white rounded-full px-2 py-0.5" data-testid={`journey-count-${col.key}`}>{list.length}</span></div>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">{list.length ? list.map((c) => <CustomerCard key={c.user_id} c={c} onNudge={setNudge} />) : <p className="text-xs text-slate-400 text-center py-4">No one here</p>}</div>
            </div>
          );
        })}
      </div>
      {nudge && <NudgeDialog customer={nudge} onClose={() => setNudge(null)} onSent={load} />}
    </div>
  );
}

export default function Playbook() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <PlaybookPage />;
}

function PlaybookPage() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "guide");
  const [pending, setPending] = useState(null);
  useEffect(() => { api.get("/admin/nudge-drafts").then((r) => setPending(r.data.length)).catch(() => {}); }, []);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [industry, setIndustry] = useState("");
  useEffect(() => { api.get("/admin/playbook").then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e))); }, []);
  const pdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/playbook/pdf", { params: industry ? { industry } : {}, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `GrantVerify_Team_Playbook${industry ? `_${industry.replace(/[^A-Za-z]+/g, "_")}` : ""}.pdf`;
      a.click();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Team playbook" title="The customer journey to grant funding"
        action={<Button variant="outline" disabled={busy} onClick={pdf} data-testid="playbook-pdf-button">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF</Button>}>
        Every stage a customer goes through on GrantVerify, why it matters to funders, and what our team does to help.
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-6">
        {[["guide", "Walkthrough", Target], ["board", "Customer journey board", Users], ["approvals", `Nudge approvals${pending ? ` (${pending})` : ""}`, Inbox]].map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`playbook-tab-${k}`} className={`flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"}`}><I className="h-4 w-4" />{l}</button>
        ))}
      </div>
      {tab === "approvals" ? <NudgeApprovals onCount={setPending} /> : tab === "board" ? <Board /> : d ? <Guide d={d} industry={industry} setIndustry={setIndustry} /> : <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />}
    </div>
  );
}
